import { useState } from 'react';
import { z } from 'zod';
import { useAuth } from '@/hooks/useAuth';

const SignupSchema = z.object({
  email: z.string().email({ message: 'Invalid email address' }),
});

export function useSignup(returnTo?: string) {
  const { requestOTP: authRequestOTP, verifyOtp: authVerifyOtp, loading: authLoading } = useAuth();

  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [formErrors, setFormErrors] = useState<z.ZodFormattedError<typeof SignupSchema._input> | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(true);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Form validation state
  const isFormValid = email.trim() !== '' && email.includes('@');

  const handleSignup = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormErrors(null);
    setApiError(null);
    setMessage(null);

    const validationResult = SignupSchema.safeParse({ email });

    if (!validationResult.success) {
      setFormErrors(validationResult.error.format());
      return;
    }

    try {
      // With Better Auth, we just request OTP - no user is created until verification
      const { error: signUpError } = await authRequestOTP(validationResult.data.email);
      if (signUpError) throw signUpError;

      setShowForm(false);
      setShowOtpInput(true);
      setMessage('Please check your email for the verification code to complete signup.');
    } catch (err: unknown) {
      let errMsg = 'An unexpected error occurred.';
      if (typeof err === 'object' && err !== null && 'message' in err && typeof err.message === 'string') {
        errMsg = err.message;
      } else if (err instanceof Error) {
        errMsg = err.message;
      }
      setApiError(errMsg);
      console.error('Signup failed:', err);
    }
  };

  const handleOtpVerification = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setApiError(null);
    setMessage(null);

    if (!otpCode.trim()) {
      setApiError('Please enter the verification code.');
      return;
    }

    try {
      const { error: verifyError } = await authVerifyOtp(email, otpCode);
      if (!verifyError) {
        if (returnTo) {
          window.location.href = returnTo;
          return;
        }
        setShowOtpInput(false);
        setIsLoggedIn(true);
        setMessage('Email verified successfully! You are now logged in.');
      }
    } catch (err: unknown) {
      let errMsg = 'An unexpected error occurred.';
      if (typeof err === 'object' && err !== null && 'message' in err && typeof err.message === 'string') {
        errMsg = err.message;
      } else if (err instanceof Error) {
        errMsg = err.message;
      }
      setApiError(errMsg);
      console.error('OTP verification failed:', err);
    }
  };

  return {
    email,
    setEmail,
    otpCode,
    setOtpCode,
    formErrors,
    apiError,
    message,
    loading: authLoading,
    showForm,
    showOtpInput,
    isLoggedIn,
    isFormValid,
    handleSignup,
    handleOtpVerification,
  };
}

// Export the return type of the hook
export type UseSignupReturn = ReturnType<typeof useSignup>; 